import router from '@/router';
import { defineStore } from 'pinia';
import { HOME_URL } from '@/config';

export interface TabItem {
  label: string;
  path: string;
  name: string;
  componentName: string;
  disableClose: boolean;
}

export const useTabbarsStore = defineStore('tabbars', {
  persist: {
    storage: localStorage,
    beforeHydrate: ctx => {
      const currentVersion = '1.0.1';
      const savedVersion = localStorage.getItem('tabbarsVersion');
      if (savedVersion !== currentVersion) {
        ctx.store.clearCache();
        localStorage.setItem('tabbarsVersion', currentVersion);
      }
    },
  },
  state: () => {
    return {
      currentTabItem: <TabItem | null>null,
      tabItems: <TabItem[]>[],
    };
  },
  getters: {
    // 获取选项卡state数据变量
    getTabs(state) {
      return state.tabItems;
    },
    getKeepAliveItems(state) {
      const items = (state.tabItems ?? [])
        .filter(item => item.componentName)
        .map(item => item.componentName)
        .flat();
      return items;
    },
  },
  actions: {
    clearCache() {
      localStorage.removeItem('tabbars');
      this.$reset(); // 重置store到初始状态
    },
    // 添加选项卡数据
    async addTabItem(item: TabItem) {
      const hasAdded = this.tabItems.some((citem: any) => {
        if (citem.name === item.name) {
          citem.path = item.path;
          citem.label = item.label;
          citem.componentName = item.componentName;
          return true;
        }
        return false;
      });
      if (hasAdded) {
        return;
      }
      this.tabItems.push(item);
    },

    async removeTabItem(item: TabItem) {
      const isCurrent = item.name === this.currentTabItem?.name;
      const tabIndex = this.tabItems.findIndex(citem => compareName(citem.name, item.name));
      this.tabItems.splice(tabIndex, 1);
      if (isCurrent) {
        const nextSelectedIndex = Math.min(tabIndex, this.tabItems.length - 1);
        if (nextSelectedIndex === -1) {
          router.push(HOME_URL);
          return;
        }
        router.push(this.tabItems[nextSelectedIndex].path);
        return;
      }
    },

    async removeTab(name: string) {
      const item = this.tabItems.find((citem: any) => compareName(citem.name, name));
      if (item) {
        this.removeTabItem(item);
      }
    },

    async removeLeftTabItems(item: TabItem) {
      const isCurrent = item.name === this.currentTabItem?.name;
      const index = this.tabItems.findIndex((citem: any) => compareName(citem.name, item.name));
      const itemsToKeep = this.tabItems.filter((citem, i) => i >= index || citem.disableClose);
      this.tabItems = itemsToKeep;
      if (!isCurrent) {
        router.push(item.path);
      }
    },

    async removeRightTabItems(item: TabItem) {
      const isCurrent = item.name === this.currentTabItem?.name;
      const index = this.tabItems.findIndex((citem: any) => compareName(citem.name, item.name));
      const itemsToKeep = this.tabItems.filter((citem, i) => i <= index || citem.disableClose);
      this.tabItems = itemsToKeep;
      if (!isCurrent) {
        router.push(item.path);
      }
    },

    async removeOtherTabItems(item: TabItem) {
      const isCurrent = item.name === this.currentTabItem?.name;
      const index = this.tabItems.findIndex((citem: any) => compareName(citem.name, item.name));
      // 保留当前项和不可关闭的项
      const itemsToKeep = this.tabItems.filter((citem, i) => i === index || citem.disableClose);
      // 删除其他项
      this.tabItems = itemsToKeep;
      if (!isCurrent) {
        router.push(item.path);
      }
    },

    async activeTabItem(item: TabItem) {
      this.currentTabItem = item;
    },
    async removeAllTabItems() {
      this.tabItems = [];
      this.currentTabItem = null;
      router.push(HOME_URL);
    },
    itemOfName(name: string) {
      return this.tabItems.find((citem: any) => compareName(citem.name, name));
    },
    originalSetup() { },
  },
});

function comparePath(a: string, b: string) {
  const path1 = a.split('?')[0];
  const path2 = b.split('?')[0];
  return path1 === path2;
}

function compareName(a?: string, b?: string) {
  if (!a && !b) {
    return true;
  }
  return a === b;
}
